import { ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { CollectionViewGroup, Globalize } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellType, GridPanel, GroupRow } from '@grapecity/wijmo.grid'

import { BaseComponent, Grid, ListItem } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { GridColumnGroupComponent } from '../../shared/reusable-components/grid-column-group/grid-column-group.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'

@Component({
    templateUrl: './productivity-summary.component.html',
    styleUrls: ['./productivity-summary.component.css'],
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        GridExportComponent,
        WjInputModule,
        GridColumnGroupComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        GridSelectionCalculationComponent,
    ],
})
export class ProductivitySummaryComponent extends BaseComponent implements OnDestroy {
    cdr = inject(ChangeDetectorRef)

    /** Debounced because programmatic updates fire `checkedItemsChanged` too — duplicate requests abort prior HTTP via cancelableRequest (server sees connection reset). */
    private phaseListDebounceTimer: ReturnType<typeof setTimeout> | undefined
    private reportDataDebounceTimer: ReturnType<typeof setTimeout> | undefined
    private readonly phaseListDebounceMs = 150
    private readonly reportDataDebounceMs = 150

    faSpinner = faSpinner
    grid: Grid<any>

    allPms: any[] = []
    pmList: ListItem<number>[] = []
    selectedPm?: number

    jobTypeList: ListItem<string>[] = []
    selectedJobTypes: string[] = []

    superintendentList: ListItem<string>[] = []
    selectedSuperintendents: string[] = []

    activeJobsList: ListItem<string>[] = []

    activeList: ListItem<number>[] = [
        new ListItem(1, 'Active'),
        new ListItem(2, 'Soft Closed'),
        new ListItem(3, 'Hard Closed'),
        new ListItem(0, 'Pending'),
    ]

    costTypeList: ListItem<number>[] = [
        new ListItem(10, 'Labor'),
        new ListItem(30, 'Equipment'), // On the server, the 30 will also include 31, which is Equipment - Other
    ]
    selectedCostType = 10

    filterByProgressCompleteList: ListItem<boolean>[] = [new ListItem(false, 'No'), new ListItem(true, 'Yes')]
    selectedFilterByProgressComplete = false

    assumeHardClosedCompleteList: ListItem<boolean>[] = [new ListItem(false, 'No'), new ListItem(true, 'Yes')]
    selectedAssumeHardClosedComplete = false

    /** FS (Field Support) phase filter: Include, Exclude, or FS Only. Matches bid estimate/cost/variance labor exclusion. */
    fsPhaseFilterList: ListItem<string>[] = [
        new ListItem('Include', 'Include'),
        new ListItem('Exclude', 'Exclude'),
        new ListItem('FsOnly', 'FS Only'),
    ]
    selectedFsPhaseFilter = 'Include'

    activityCodeList: ListItem<string>[] = []
    phaseDescriptions: Record<string, string> = {}

    constructor() {
        super()
        this.activeList[0].$checked = true
        this.grid = new Grid<any>(this.services, 'id', this)
            .wrapColumnHeaders(80)
            .isReadOnly(true)
            .editOnDoubleClick(false)
            .showAggregateRow(true)
        this.getParameters()
    }

    override ngOnDestroy(): void {
        if (this.phaseListDebounceTimer !== undefined) clearTimeout(this.phaseListDebounceTimer)
        if (this.reportDataDebounceTimer !== undefined) clearTimeout(this.reportDataDebounceTimer)
        super.ngOnDestroy()
    }

    allSuperintendents: any[] = []

    /**
     * Fetches and processes parameter values to populate the action toolbar options.
     */
    getParameters() {
        this.cancelableRequest(
            'parameters',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.get('/api/ProductivityAnalysis/GetPms'),
                    this.services.data.get('/api/ProductivityAnalysis/GetPhaseDescriptions'),
                    this.services.data.get('/api/VpData/QueryProjectTypes'),
                    this.services.data.get('/api/User/GetSupers'),
                    this.services.data.vpJobs.queryAllByAction('GetAllJobs'),
                )
                .subscribe({
                    next: result => {
                        this.allPms = result[0]
                        const phaseDescriptions = result[1] as any[]
                        this.phaseDescriptions = phaseDescriptions.toObject(
                            x => this.extractFourDigits(x.phase) ?? x.phase,
                            x => x.description ?? 'Unknown or Duplicate Phase',
                        )
                        const projectTypes = result[2] as any[]
                        this.jobTypeList = projectTypes.map(x => new ListItem(x.databaseValue, x.databaseValue))
                        this.allSuperintendents = result[3] as any[]
                        this.updateProjectManagerList()
                        this.cdr.detectChanges()
                        setTimeout(() => {
                            this.initialLoadingComplete()
                            //this.refresh()
                        }, 10)
                    },
                }),
        )
    }

    updateJobTypeList() {
        const selectedJobTypes = this.jobTypeList.filter(x => x.$checked).map(x => x.value)
        this.selectedJobTypes = selectedJobTypes
        // updateProjectManagerList already cascades to superintendent + job lists; avoid calling them
        // again here or cancelableRequest will abort in-flight phase/report HTTP (server sees connection reset).
        this.updateProjectManagerList()
    }

    updateSuperintendentList() {
        const previousSuperValues = this.superintendentList.filter(x => x.$checked).map(x => x.value)
        const selectedStatuses = this.activeList.filter(x => x.$checked).map(x => x.value)
        const selectedJobTypes = this.jobTypeList.filter(x => x.$checked).map(x => x.value)
        const selectedPms = this.pmList.filter(x => x.$checked).map(x => x.value)

        let jobsList = this.services.data.vpJobs.items.slice().filter(x => selectedStatuses.contains(x.jobStatus))

        if (selectedJobTypes.length > 0) {
            jobsList = jobsList.filter(x => selectedJobTypes.contains(x.jobType))
        }

        if (selectedPms.length > 0) {
            jobsList = jobsList.filter(x => selectedPms.contains(x.projectMgr))
        }

        // Get unique superintendents from the filtered jobs
        const superintendentHrRefs = jobsList
            .map(x => x.siteSuperUserId)
            .filter(x => x != null && x !== undefined && x !== 0)
            .distinct()

        // Match with all superintendents to get names
        // UserDto from API: properties are camelCase (hrRef, displayName, name) after JSON deserialization
        if (!this.allSuperintendents || this.allSuperintendents.length === 0) {
            this.superintendentList = []
            return
        }

        this.superintendentList = this.allSuperintendents
            .filter(x => {
                // Handle both camelCase and PascalCase property names
                const hrRef = (x as any).hrRef ?? (x as any).HrRef
                return hrRef != null && superintendentHrRefs.contains(hrRef)
            })
            .map(x => {
                const hrRef = (x as any).hrRef ?? (x as any).HrRef
                const displayName = (x as any).displayName ?? (x as any).DisplayName ?? ''
                const name = (x as any).name ?? (x as any).Name ?? ''
                return new ListItem(hrRef.toString(), displayName || name || `Superintendent ${hrRef}`)
            })
            .sort((a, b) => a.display.localeCompare(b.display))

        this.restoreChecks(this.superintendentList, previousSuperValues)
    }

    updateJobList() {
        const previousJobValues = this.activeJobsList.filter(x => x.$checked).map(x => x.value)
        const selectedStatuses = this.activeList.filter(x => x.$checked).map(x => x.value)
        const selectedPms = this.pmList.filter(x => x.$checked).map(x => x.value)
        const selectedJobTypes = this.jobTypeList.filter(x => x.$checked).map(x => x.value)
        const selectedSuperintendents = this.superintendentList.filter(x => x.$checked).map(x => parseInt(x.value))
        const excludedJobIds = ['14924.', '14701.']

        let jobsList = this.services.data.vpJobs.items
            .slice()
            .filter(x => selectedStatuses.contains(x.jobStatus))
            .filter(x => selectedJobTypes.length === 0 || selectedJobTypes.contains(x.jobType))
            .filter(x => selectedPms.contains(x.projectMgr))
            .filter(
                x =>
                    selectedSuperintendents.length === 0 ||
                    (x.siteSuperUserId != null && selectedSuperintendents.contains(x.siteSuperUserId)),
            )
            .filter(x => !excludedJobIds.includes(x.id.trim()))
            .map(x => new ListItem(x.id.trim(), x.id.trim() + ' - ' + x.name))

        jobsList = jobsList.orderBy(x => x.display)

        jobsList.forEach(x => (x.selected = true))
        this.activeJobsList = jobsList

        const restored = this.restoreChecks(this.activeJobsList, previousJobValues)
        if (restored) {
            this.updatePhaseList()
        }
    }

    updateProjectManagerList() {
        const previousPmValues = this.pmList.filter(x => x.$checked).map(x => x.value)
        // Based on the jobs that passed the job Status and Job Type filters, we will filter the PMs
        const selectedStatuses = this.activeList.filter(x => x.$checked).map(x => x.value)
        const selectedJobTypes = this.jobTypeList.filter(x => x.$checked).map(x => x.value)

        let jobsList = this.services.data.vpJobs.items.slice().filter(x => selectedStatuses.contains(x.jobStatus))
        if (selectedJobTypes.length > 0) {
            jobsList = jobsList.filter(x => selectedJobTypes.contains(x.jobType))
        }

        const jobPms = jobsList.map(x => x.projectMgr).distinct()
        this.pmList = this.allPms
            .slice()
            .filter(x => jobPms.contains(x.projectMgr))
            .map(x => new ListItem(x.projectMgr, x.name))

        this.restoreChecks(this.pmList, previousPmValues)

        // Update superintendent list and job list when PM list changes
        this.updateSuperintendentList()
        this.updateJobList()
    }

    /** Schedules phase load; collapses back-to-back calls from code + Wijmo `checkedItemsChanged`. */
    updatePhaseList() {
        if (this.phaseListDebounceTimer !== undefined) clearTimeout(this.phaseListDebounceTimer)
        this.phaseListDebounceTimer = setTimeout(() => {
            this.phaseListDebounceTimer = undefined
            this.loadPhaseList()
        }, this.phaseListDebounceMs)
    }

    private loadPhaseList() {
        const jobs = this.activeJobsList.filter(x => x.$checked).map(x => x.value) as string[]
        if (jobs.length === 0) {
            this.activityCodeList = []
            return
        }

        const previousActivityValues = this.activityCodeList.filter(x => x.$checked).map(x => x.value)

        const queryDto = {
            costType: this.selectedCostType,
            jobs: jobs,
            phases: [],
        }

        this.cancelableRequest(
            'phases',
            true,
            this.services.data.load(true, true, this.services.data.post('/api/ProductivityAnalysis/GetPhasesByJobs', queryDto)).subscribe({
                next: successResult => {
                    this.activityCodeList = successResult[0].map(x => new ListItem(x.key, x.key + ' - ' + x.value))
                    if (this.restoreChecks(this.activityCodeList, previousActivityValues)) {
                        this.getReportData()
                    }
                },
            }),
        )
    }

    /**
     * Fetches the Productivity Summary data based on the selected parameters in the action toolbar.
     */
    getReportData() {
        if (this.reportDataDebounceTimer !== undefined) clearTimeout(this.reportDataDebounceTimer)
        this.reportDataDebounceTimer = setTimeout(() => {
            this.reportDataDebounceTimer = undefined
            this.runReportData()
        }, this.reportDataDebounceMs)
    }

    private runReportData() {
        if (this.isInitialLoading || this.activityCodeList.filter(x => x.$checked).length === 0) {
            this.grid.items = []
            return
        }

        const queryDto = {
            costType: this.selectedCostType,
            jobs: this.activeJobsList.filter(x => x.$checked).map(x => x.value.trim()),
            phases: this.activityCodeList.filter(x => x.$checked).map(x => x.value),
            filterByProgressComplete: this.selectedFilterByProgressComplete,
            assumeHardClosedComplete: this.selectedAssumeHardClosedComplete,
            fsPhaseFilter: this.selectedFsPhaseFilter,
        }

        this.cancelableRequest(
            'data',
            true,
            this.services.data
                .load(true, true, this.services.data.post('/api/ProductivityAnalysis/GetProductivitySummaryData', queryDto))
                .subscribe({
                    next: result => {
                        this.grid.items = result[0]
                    },
                }),
        )
    }

    private restoreChecks<T>(list: ListItem<T>[], previousValues: T[]): boolean {
        if (previousValues.length === 0) return false
        const valid = new Set(list.map(x => x.value))
        const toRestore = new Set(previousValues.filter(v => valid.has(v)))
        if (toRestore.size === 0) return false
        list.forEach(x => {
            if (toRestore.has(x.value)) x.$checked = true
        })
        return true
    }

    gridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const row = panel.grid.rows[r]
            const col = panel.grid.columns[c] as any
            const rowItem = row.dataItem

            cell.style.color = 'unset'
            cell.style.borderRight = ''
            cell.style.borderLeft = ''

            // cell.style.borderRightWidth = '1px'
            // cell.style.borderLeftWidth = '1px'
            if (rowItem[col.binding] < 0) {
                cell.style.color = 'red'
            }

            if (col.binding === 'percentUsed' || col.binding === 'projectedHours') {
                cell.style.borderRight = '2px solid black'
            }

            if (col.binding === 'totalHours') {
                cell.style.borderLeft = '2px solid black'
            }
        }
    }

    extractFourDigits(value: string): string | null {
        const match = value.match(/\.(\d{4})\./)
        return match ? match[1] : null
    }

    onFormatItem(e: any) {
        const panel = e.panel
        const row = e.row
        const col = e.col
        const grid = panel.grid
        const colBinding = panel.columns[col].binding

        // Check if it's the columnFooter (bottom aggregate row)
        if (panel.cellType === CellType.ColumnFooter) {
            // Calculate custom percentage
            if (colBinding === 'percentUsed' || colBinding === 'efficiencyRate') {
                const estHours = this.getColumnAggregate(grid.collectionView.items, 'estHours')
                if (colBinding === 'percentUsed') {
                    const totalHours = this.getColumnAggregate(grid.collectionView.items, 'totalHours')
                    this.setPercentCell(e.cell, totalHours, estHours)
                } else if (colBinding === 'efficiencyRate') {
                    const projectedHours = this.getColumnAggregate(grid.collectionView.items, 'projectedHours')
                    this.setPercentCell(e.cell, projectedHours, estHours, 2)
                }
            }
        }

        // Handle group footer row
        if (
            panel.cellType === CellType.Cell &&
            panel.rows[row] instanceof GroupRow &&
            (colBinding === 'percentUsed' || colBinding === 'efficiencyRate')
        ) {
            const groupRow = panel.rows[row] as GroupRow
            const group = groupRow.dataItem as CollectionViewGroup
            if (group && group.items) {
                const estHours = this.getColumnAggregate(group.items, 'estHours')
                if (colBinding === 'percentUsed') {
                    const totalHours = this.getColumnAggregate(group.items, 'totalHours')
                    this.setPercentCell(e.cell, totalHours, estHours)
                } else if (colBinding === 'efficiencyRate') {
                    const projectedHours = this.getColumnAggregate(group.items, 'projectedHours')
                    this.setPercentCell(e.cell, projectedHours, estHours, 2)
                }
            }
        }
    }

    // Helper to calculate column sum
    getColumnAggregate(items: any[], columnBinding: string): number {
        let sum = 0
        items.forEach(item => {
            const val = item[columnBinding]
            if (typeof val === 'number' && !isNaN(val)) {
                sum += val
            }
        })
        return sum
    }

    setPercentCell(cell: HTMLElement, numerator: number, denominator: number, offset = 0) {
        if (denominator) {
            const percent = offset - numerator / denominator
            if (percent < 0) {
                cell.style.color = 'red'
            }
            cell.textContent = Globalize.format(percent, 'P2')
        } else {
            cell.textContent = ''
        }
    }
}
